/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, no-prototype-builtins, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { snapshotFields } from '@linc-technologies/linced-common/utils/record-rollback';

export default class CaregiverFinanceAccessController extends Controller {
  @action
  setContactFilter(value) {
    this.contactFilter = value;
  }

  @action
  setFirstNameFilter(value) {
    this.firstNameFilter = value;
  }

  @action
  setHomeroomFilter(value) {
    this.homeroomFilter = value;
  }

  @action
  setLastNameFilter(value) {
    this.lastNameFilter = value;
  }

  @action
  setNotesFilter(value) {
    this.notesFilter = value;
  }

  @action
  setSalutationFilter(value) {
    this.salutationFilter = value;
  }

  @tracked
  contactFilter;

  @tracked
  firstNameFilter;

  @tracked
  homeroomFilter;

  @tracked
  lastNameFilter;

  @tracked
  notesFilter;

  @tracked
  salutationFilter;

  // Services
  @service
  paperToaster;

  @service
  store;

  Scopes = Scopes;

  @computed('model.{accounts.[],learners.[]}')
  get accountsByLearner() {
    let map = this.model.learners.reduce((map, learner) => {
      map[learner.id] = [];
      return map;
    }, {});
    return this.model.accounts.reduce((map, account) => {
      let personId = account.person_id;
      if (map.hasOwnProperty(personId)) {
        map[personId].push(account);
      }

      return map;
    }, map);
  }

  @computed(
    'model.learners.[]',
    'lastNameFilter',
    'firstNameFilter',
    'homeroomFilter',
    'contactFilter',
    'salutationFilter',
    'notesFilter',
  )
  get filteredLearners() {
    let learners = this.model.learners;

    let filterLastName = !isEmpty(this.lastNameFilter);
    let filterFirstName = !isEmpty(this.firstNameFilter);
    let filterHomeroom = !isEmpty(this.homeroomFilter);
    let filterContacts = !isEmpty(this.contactFilter);
    let filterSalutation = !isEmpty(this.salutationFilter);
    let filterNotes = !isEmpty(this.notesFilter);
    if (
      filterLastName ||
      filterFirstName ||
      filterHomeroom ||
      filterContacts ||
      filterSalutation ||
      filterNotes
    ) {
      learners = learners.filter((person) => {
        if (filterLastName) {
          let fieldValue = person.get('lastName') ?? '';
          if (!filterValue(fieldValue, DataType.STRING, this.lastNameFilter)) {
            return false;
          }
        }
        if (filterFirstName) {
          let fieldValue = person.get('firstName') ?? '';
          if (!filterValue(fieldValue, DataType.STRING, this.firstNameFilter)) {
            return false;
          }
        }
        if (filterHomeroom) {
          let fieldValue = person.get('homeroom.name') ?? '';
          if (!filterValue(fieldValue, DataType.STRING, this.homeroomFilter)) {
            return false;
          }
        }
        if (filterContacts) {
          let accounts = this.get(`accountsByLearner.${person.id}`) ?? [];
          let caregiverIds = accounts.reduce((ids, account) => {
            ids.push(...account.caregiver_ids);
            return ids;
          }, []);

          if (this.contactFilter.trim() === '!*') {
            // Filter for learners with no contacts.
            return person.get('learner.contacts').every((contact) => {
              // Those who don't have access, don't have user accounts, or don't have finance access don't count.
              return (
                !contact.allowedAccess ||
                isEmpty(contact.get('caregiver.userId')) ||
                !caregiverIds.includes(contact.caregiverId)
              );
            });
          }

          return person.get('learner.contacts').any((contact) => {
            if (
              !contact.allowedAccess ||
              isEmpty(contact.get('caregiver.userId')) ||
              !caregiverIds.includes(contact.caregiverId)
            ) {
              // Not allowed access, no user account, or no finance access - skip.
              return false;
            }

            // Include this learner if any contact's name matches the filter.
            let contactName = contact.get('fullName');
            return filterValue(
              contactName,
              DataType.STRING,
              this.contactFilter,
            );
          });
        }
        if (filterSalutation) {
          let accounts = this.get(`accountsByLearner.${person.id}`) ?? [];
          return accounts.some((account) => {
            return filterValue(
              account.salutation,
              DataType.STRING,
              this.salutationFilter,
            );
          });
        }
        if (filterNotes) {
          let accounts = this.get(`accountsByLearner.${person.id}`) ?? [];
          return accounts.some((account) => {
            return filterValue(
              account.notes,
              DataType.STRING,
              this.notesFilter,
            );
          });
        }

        // Matched all specified filters - include this record.
        return true;
      });
    }

    return learners;
  }

  @computed('model.learners.[]', 'accountsByLearner')
  get unsyncedLearners() {
    let learners = this.model.learners;

    return learners.filter((person) => {
      let accounts = this.get(`accountsByLearner.${person.id}`) ?? [];
      let caregiverIds = accounts.reduce((ids, account) => {
        ids.push(...account.caregiver_ids);
        return ids;
      }, []);

      return person.get('learner.primaryContacts').some((contact) => {
        if (
          contact.allowedAccess &&
          contact.billPayer &&
          !caregiverIds.includes(contact.caregiverId)
        ) {
          // Bill payer flag set, but not allowed access.
          return true;
        }

        return false;
      });
    });
  }

  @tracked
  fixAccessDialogOpen = false;
  @tracked
  fixAccessInProgress = false;
  @tracked fixAccessNumProcessed = 0;
  @tracked fixAccessNumUnsynced = 0;

  // fixAccessProcessedPercent calculates the percentage of students updated to inform
  // a determinate loading component.
  get fixAccessProcessedPercent() {
    if (this.fixAccessNumUnsynced > 0) {
      return (this.fixAccessNumProcessed / this.fixAccessNumUnsynced) * 100;
    }
    return 0;
  }

  @action
  fixAccess() {
    this.fixAccessNumUnsynced = this.unsyncedLearners.length;
    this.fixAccessNumProcessed = 0;
    this.fixAccessInProgress = true;
    this.fixAccessDialogOpen = true;

    const learners = this.unsyncedLearners;

    return learners
      .reduce((previousPromise, nextLearner) => {
        let accounts = this.get(`accountsByLearner.${nextLearner.id}`) ?? [];
        if (!isEmpty(accounts) && accounts.length === 1) {
          return previousPromise.then(async () => {
            let account = accounts[0];
            // caregiverIds is an async hasMany: its PromiseManyArray can't be
            // mutated, so build the new list and assign it.
            let caregivers = (await account.caregiverIds).slice();
            nextLearner.get('learner.primaryContacts').forEach((contact) => {
              if (
                contact.billPayer &&
                !caregivers.some((c) => c.id === contact.caregiverId)
              ) {
                caregivers.push(
                  this.store.peekRecord('person', contact.caregiverId),
                );
              }
            });
            let restore = snapshotFields(account, ['caregiverIds']);
            account.set('caregiverIds', caregivers);

            return account.save().then(
              () => {
                this.incrementProperty('fixAccessNumProcessed');
              },
              (err) => {
                // The refetch below no longer drops unsaved hasMany edits.
                restore();
                throw err;
              },
            );
          });
        }

        return previousPromise.then(() => {
          this.incrementProperty('fixAccessNumProcessed');
          return Promise.resolve();
        });
      }, Promise.resolve())
      .then(() => {
        this.fixAccessInProgress = false;
        this.fixAccessDialogOpen = false;

        this.paperToaster.show(`Caregiver finance access updated.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
      })
      .catch(() => {
        this.paperToaster.show(
          `Error updating finance access. Please try again.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
      })
      .finally(() => {
        this.model.accounts.update();
      });
  }
}
